import { api } from '../../../plugins/axios';
import { dateTimeFormatter } from '../../../utils/index';
import { t } from '../../../../locales';
import { InvoiceStatus, PaymentStatus } from './enums';
import { Alert } from '@/v2/plugins/alert';
import Shipment from './shipment';
export default class Order {
	id = undefined;
	order_number = undefined;
	created_at = undefined;
	main_order_items = undefined;
	order_total_amount = undefined;
	order_total_currency = undefined;
	variant = undefined;
	invoices = undefined;
	order_status = undefined;
	getItemsLoading = false;

	constructor(obj) {
		Object.keys(obj).forEach((key) => {
			this[key] = obj[key];
		});
		if (obj.shipments) {
			this.shipments = obj.shipments.map((shipment) => new Shipment(shipment));
			// Inject price from main_order_items
			this.shipments.forEach((shipment) => {
				shipment.shipping_items = shipment.shipping_items.map((item) => {
					const product = this.main_order_items.find(
						(mainItem) => mainItem.variant_id === item.variant_id,
					);
					return {
						...item,
						unit_price: product?.unit_price || null,
					};
				});
			});
		}
	}

	get orderStatusValue() {
		if (typeof this.order_status === 'string') return this.order_status;
		return this.order_status?.main_order_status;
	}

	static async getOrders(params) {
		const res = await api.get('/v1/orders', {
			params: {
				...params.filters,
				...params,
			},
		});
		return {
			data: res.data.result.orders.data.map((item) => new Order(item)),
			totalRecords: res.data.result.orders.total,
		};
	}

	static async getOrdersList(params) {
		const res = await api.get('/main-order/orders', {
			params,
		});
		return {
			data: res.data.result.orders.data.map((item) => new Order(item)),
			totalRecords: res.data.result.orders.meta.total,
		};
	}

	static async getById(id) {
		const res = await api.get(`v1/orders/${id}`);
		const order = res.data.result.orders;
		return new Order(order);
	}

	static async getOrderDetailsById(id) {
		const res = await api.get(`/main-order/orders/${id}`);
		const order = res.data.result.order;
		return new Order(order);
	}

	async generatePaymentLink() {
		const res = await api.get(`main-order/payment/generate-link/${this.id}`);
		const { link, expiry_date, success, msg } = res.data;
		if (!success) {
			Alert.error(msg);
			if (link) {
				window.location.href = link;
			}
			return;
		}
		this.invoices.unshift({
			invoice_status: InvoiceStatus.pending,
			invoice_url: link,
			expiry_date,
		});
		return { link, expiry_date };
	}

	getPaymentStatus() {
		const paymentActions = { link: '', status: PaymentStatus.Generate, expiryDate: '' };

		const invoices = this.invoices;
		if (!invoices || !invoices.length) {
			paymentActions.link = '';
			paymentActions.status = PaymentStatus.Generate;
			paymentActions.expiryDate = '';
			return paymentActions;
		}
		const currentInvoice = invoices[0];
		const currentDate = new Date();
		const expiryDate = new Date(currentInvoice?.expiry_date);
		const formattedExpiryDate = dateTimeFormatter(expiryDate);
		paymentActions.expiryDate = formattedExpiryDate;

		if (currentInvoice?.invoice_status == InvoiceStatus.success) {
			paymentActions.link = '';
			paymentActions.status = PaymentStatus.Paid;
		}
		if (currentInvoice?.invoice_status == InvoiceStatus.pending) {
			if (currentDate.getTime() > expiryDate.getTime()) {
				paymentActions.link = t('Expired Link');
				paymentActions.status = PaymentStatus.Expired;
			} else {
				paymentActions.link = currentInvoice.invoice_url;
				paymentActions.status = PaymentStatus.Valid;
			}
		}

		return paymentActions;
	}

	async getChangeStatusOptions() {
		const res = await api.get(`main-order/orders/${this.id}/available-statuses`);

		const data = (res.data.result?.list || []).map((item) => {
			return {
				value: item.id,
				label: item.text,
			};
		});

		return {
			data,
			totalRecords: data.length,
		};
	}

	async updateOrderStatus(status) {
		try {
			await api.put(`main-order/orders/${this.id}/update-status`, { order_status: status });
			if (typeof this.order_status === 'string') {
				this.order_status = status;
			} else {
				this.order_status.main_order_status = status;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	async cancelOrderItem(data) {
		try {
			await api.post(`/main-order/orders/${this.id}/cancel-order-items`, data);
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	async sendOrderSummaryToWhatsApp() {
		try {
			await api.post(`/main-order/payment/order-summary`, { order_id: this.id });
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	async getItems() {
		if (this.main_order_items) return;

		this.getItemsLoading = true;
		try {
			const data = await Order.getOrderDetailsById(this.id);
			this.main_order_items = data.main_order_items;
		} finally {
			this.getItemsLoading = false;
		}
	}
}
